import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CCol, CRow, CForm, CFormInput, CInputGroup, CInputGroupText, CFormLabel, CFormSelect, CButton, CAlert, CSpinner,
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
  CCard, CCardHeader, CCardBody, CBadge,
} from '@coreui/react'
import { api, currentUser } from '../api/client'
import { capitalizarPalabras } from '../utils/texto'
import { fmtMoneda, simboloMoneda } from '../utils/moneda'
import { NumericFormat } from 'react-number-format'
import WwGrid, { WwColumn, WwRowAction } from '../components/ww/WwGrid'
import WwFilterBar from '../components/ww/WwFilterBar'
import { usePendiente } from '../components/ww/pendiente'
import DevolucionFacturaModal, { DevolucionCreada } from '../components/DevolucionFacturaModal'

interface LineaForm { productoNro: string; cantidad: string }

// Select con búsqueda para el producto de la línea [RGM] 2026-10-05 (pedido
// Rafael: "necesito buscar producto en el mismo select"): input que filtra
// mientras se escribe y despliega las coincidencias; al elegir guarda el
// ProductoNro. Reemplaza al CFormSelect de lista completa (el combo GX filtra
// por nombre — mismo espíritu del datalist del panel).
function SelectorProducto({ productos, value, onChange, disabled }: {
  productos: any[]
  value: string
  onChange: (v: string) => void
  disabled?: boolean
}) {
  const [texto, setTexto] = useState('')
  const [abierto, setAbierto] = useState(false)
  const sel = productos.find((p) => String(p.productoNro) === value)
  // [RGM] 2026-10-07 (pedido Rafael: "mostrar stock"): el buscador muestra
  // también el stock del producto (productoStock del catálogo).
  const etiqueta = (p: any) => `${p.productoNombre?.trim() || 'Producto'} — ${fmtMoneda(Number(p.productoValor ?? 0))} · Stock: ${Number(p.productoStock ?? 0)}`
  const filtro = texto.trim().toLowerCase()
  const opciones = filtro
    ? productos.filter((p) => etiqueta(p).toLowerCase().includes(filtro) || String(p.productoNro).includes(filtro))
    : productos
  return (
    <div className="position-relative">
      <CFormInput
        value={abierto ? texto : (sel ? etiqueta(sel) : '')}
        placeholder="Buscar producto…"
        disabled={disabled}
        autoComplete="off"
        onFocus={() => { setAbierto(true); setTexto('') }}
        onChange={(e) => setTexto(e.target.value)}
        onBlur={() => window.setTimeout(() => setAbierto(false), 150)}
      />
      {abierto && (
        <div className="position-absolute w-100 bg-body border rounded shadow-sm"
          style={{ zIndex: 1050, top: '100%', maxHeight: 240, overflowY: 'auto' }}>
          {opciones.map((p) => (
            <div key={String(p.productoNro)} className="px-3 py-2"
              style={{ cursor: 'pointer' }}
              onMouseDown={() => { onChange(String(p.productoNro)); setAbierto(false); setTexto('') }}>
              {etiqueta(p)}
            </div>
          ))}
          {opciones.length === 0 && <div className="px-3 py-2 text-body-secondary">Sin resultados</div>}
        </div>
      )}
    </div>
  )
}

// [RGM] 2026-09-29 (pedido Rafael: "los selects están en número" + "fiel a
// GX"): etiquetas de los combos GX de facturaww.cs:3755-3802 —
// Origen: 1=POS, 2=App · Estado: 1=Abierta, 2=Activa, 3=Cancelada ·
// FPago: 1=Contado, 2=Credito, 3=Tarjeta Debito, 4=Tarjeta Credito.
// [RGM] 2026-10-14 (feature devoluciones Fase 1 — diseño aprobado:
// odd/tasks/devoluciones-facturacion.md): 5=Devolución es el marker del
// documento de devolución (Factura en negativo, ReglasFacturacion.
// FPagoDevolucion — ReglasFacturacion.cs:106).
const ORIGEN_FACTURA: Record<number, string> = { 1: 'POS', 2: 'App' }
// [RGM] 2026-10-14 (AJUSTES devoluciones, punto 4): 4 = «Devuelta totalmente»
// (ReglasFacturacion.EstadoFacturaDevueltaTotalmente = 4) — la devolución
// total CONSERVA la factura original en el historial con este estado, ya no
// Cancelada (3). El combo de Estado del formulario se arma de este mapa.
const ESTADO_FACTURA: Record<number, string> = {
  1: 'Abierta', 2: 'Activa', 3: 'Cancelada', 4: 'Devuelta totalmente',
}
const FPAGO_FACTURA: Record<number, string> = {
  1: 'Contado', 2: 'Credito', 3: 'Tarjeta Debito', 4: 'Tarjeta Credito', 5: 'Devolución',
}

// [RGM] 2026-10-07 (feature factura-credito-unificado F1, pedido Rafael):
// combos del CRÉDITO inline — MISMAS listas del modal "Configurar créditos"
// (PrestamosPage): Modalidad 1..60 (prestamo.cs:6639-6645) y Día Cobro GX
// (prestamoww.cs:4585-4595 / lista fija :5458; "(Ninguno)" = GX_EmptyItemText
// prestamo.cs:6653). Los valores que muestran etiqueta especial (15-30, 5-20,
// 3-18, DECADAL) replican las etiquetas del combo GX.
const METODO_PAGO_CREDITO: Record<number, string> = {
  1: 'Diario', 7: 'Semanal', 15: 'Quincenal', 30: 'Mensual', 60: 'Pago Libre',
}
const DIA_COBRO_CREDITO = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa', 'Do', '15', '30', 'QT', '52', '38', '10']
const DIA_COBRO_CREDITO_LABEL: Record<string, string> = {
  QT: '15-30', '52': '5-20', '38': '3-18', '10': 'DECADAL',
}

// Fecha de HOY local "yyyy-mm-dd" — mismo patrón/uso del modal de préstamo
// (PrestamosPage.hoyLocal: fechaPrestamo por defecto en el alta).
const hoyLocal = () => {
  const d = new Date()
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

const columnas: WwColumn[] = [
  // [RGM] 2026-10-14 (devoluciones Fase 1): el documento de devolución es una
  // Factura con valores NEGATIVOS (facturaFPago=5) — se distingue con badge y
  // referencia a la factura original (facturaOriginalNro; el backend lo expone
  // en GET /facturas para el doc de devolución — FacturasController.cs:192-194).
  {
    key: 'facturaNro', title: 'Nro', type: 'number',
    render: (row) => (row.esDevolucion ? (
      <span className="d-inline-flex align-items-center gap-1">
        {row.facturaNro}
        <CBadge color="warning">Devolución #{row.facturaOriginalNro}</CBadge>
      </span>
    ) : row.facturaNro),
    exportValue: (row) => (row.esDevolucion ? `Devolución de #${row.facturaOriginalNro}` : row.facturaNro),
  },
  { key: 'facturaFecha', title: 'Fecha', type: 'date' },
  { key: 'facturaHora', title: 'Hora' },
  { key: 'facturaCreadaPor', title: 'Creada Por' },
  { key: 'clienteCedula', title: 'Cliente Cedula', type: 'id' },
  { key: 'clienteNombre', title: 'Cliente Nombre' },
  { key: 'clienteCelular', title: 'Cliente Celular' },
  { key: 'clienteDireccion', title: 'Cliente Direccion' },
  { key: 'zonaNombre', title: 'Ruta Nombre' },
  { key: 'subTotal', title: 'Sub Total', type: 'currency' },
  { key: 'facturaIVA', title: 'IVA', type: 'currency' },
  // [RGM] 2026-10-03 (pedido Rafael: el .xlsx debe mostrar el TEXTO visible):
  // exportValue espeja el render (mismo mapa ORIGEN/ESTADO/FPAGO y fallback al crudo).
  {
    key: 'facturaOrigen', title: 'Origen',
    render: (row) => ORIGEN_FACTURA[Number(row.facturaOrigen)] ?? row.facturaOrigen,
    exportValue: (row) => ORIGEN_FACTURA[Number(row.facturaOrigen)] ?? row.facturaOrigen,
  },
  {
    key: 'facturaEstado', title: 'Estado',
    render: (row) => ESTADO_FACTURA[Number(row.facturaEstado)] ?? row.facturaEstado,
    exportValue: (row) => ESTADO_FACTURA[Number(row.facturaEstado)] ?? row.facturaEstado,
  },
  {
    key: 'facturaFPago', title: 'FPago',
    render: (row) => FPAGO_FACTURA[Number(row.facturaFPago)] ?? row.facturaFPago,
    exportValue: (row) => FPAGO_FACTURA[Number(row.facturaFPago)] ?? row.facturaFPago,
  },
  { key: 'facturaObservcaiones', title: 'Observcaiones' },
  { key: 'inicial', title: 'Inicial', type: 'currency' },
  { key: 'aFinanciar', title: 'a financiar', type: 'currency' },
  // El Descuento del encabezado es el PORCENTAJE (FacturaLibreValor3 — caption
  // GX "Descuento", facturaww.cs:2832; combo 5%..70% en
  // productoproductofacturawc.cs:3165-3179). [RGM] 2026-10-05 (pedido Rafael:
  // "columna descuento va %").
  {
    key: 'descuento', title: 'Descuento',
    render: (row) => `${Number(row.descuento ?? 0)}%`,
    exportValue: (row) => `${Number(row.descuento ?? 0)}%`,
  },
]

const FacturasPage: React.FC = () => {
  const navigate = useNavigate()
  const user = currentUser()
  const empresa = user?.companyId ?? '01'
  const [rows, setRows] = useState<any[]>([])
  const formRef = useRef<HTMLFormElement>(null)
  // ── Filtros rápidos (WwFilterBar) ──
  const [filtroCreador, setFiltroCreador] = useState('')
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [abierto, setAbierto] = useState(false)
  const { aviso, setAviso } = usePendiente()
  const [guardando, setGuardando] = useState(false)
  const [errorModal, setErrorModal] = useState('')
  // [RGM] 2026-10-14 (devoluciones Fase 1 — diseño aprobado): fila abierta en
  // el modal de devolución (null = cerrado) + último documento creado, para el
  // mensaje de éxito con la acción "Imprimir devolución".
  const [devolucionDe, setDevolucionDe] = useState<any | null>(null)
  const [devolucionCreada, setDevolucionCreada] = useState<DevolucionCreada | null>(null)
  // Fila en edición (facturaww.cs:2928 'DO UPDATE' → webobject factura Mode="UPD")
  const [editando, setEditando] = useState<any | null>(null)
  // [RGM] 2026-09-29 (pedido Rafael: "quitar campos que no se usan"): fuera
  // Libre Carácter 1/2 y Geolocalización del formulario.
  // [RGM] 2026-10-05 (pedido Rafael): Origen default 1=POS, Estado default
  // 2=Activa y FPago default 1=Contado en el ALTA (editables;
  // facturaencabezadosd.cs:2892-2893, factura.cs:5561-5563). El FPago=1 lo
  // pidió Rafael (2026-10-05) — en GX arranca en 0 (factura.cs:5053).
  // En edición se conservan los de la factura.
  const [form, setForm] = useState({
    empresaCodigo: empresa, clienteCedula: '', iva: '', porcentajeDescuento: '',
    facturaOrigen: '1', facturaEstado: '2', facturaFPago: '1',
    facturaLibreValor1: '', facturaObservcaiones: '',
  })
  // [RGM] 2026-10-07 (feature factura-credito-unificado F1 — pedido Rafael:
  // "mostrar los configurables de configurar crédito en la misma pantalla,
  // menos los que ya viajan de la factura"): configurables del CRÉDITO inline
  // — espejo de los EDITABLES del modal (PrestamosPage): Modalidad, Día Cobro,
  // Cuotas, Cuota, Fecha Préstamo, Próximo Cobro, Acepta Condenas (+Valor/
  // NroAtrasos) y Solicita Renovar. Defaults idénticos al modal (metodoPago 1
  // Diario, fechaPrestamo hoy, demás vacíos). Lo que YA viaja de la factura
  // (cliente —y su ruta—, valor a financiar, factura Nro, empresa) no se
  // captura aquí.
  const creditoInicial = () => ({
    metodoPago: '1', prestamoDiaCobro: '', cuotas: '', cuota: '',
    fechaPrestamo: hoyLocal(), fechaProximoCobro: '',
    aceptaCondenas: 'No', valorCondena: '', nroAtrasosPorCondena: '',
    feriados: '', prestamoLibreNumerico2: '0',
  })
  const [credito, setCredito] = useState(creditoInicial())
  // Empresa que trabaja festivos: oculta Feriados (misma regla del modal —
  // PrestamosPage [RGM] 2026-10-02: el cálculo no suma feriados con ese flag).
  const [trabajaFestivo, setTrabajaFestivo] = useState(false)
  const isAdminFactura = ['superadmin', 'companyadmin'].includes(user?.role ?? '')
  const [lineas, setLineas] = useState<LineaForm[]>([{ productoNro: '', cantidad: '' }])
  // [RGM] 2026-09-29 (pedido Rafael: selects con nombre): clientes y
  // productos para los combos del formulario.
  const [clientes, setClientes] = useState<any[]>([])
  const [productos, setProductos] = useState<any[]>([])
  // [RGM] 2026-10-05 (pedido Rafael: "traer datos cliente como GX"): datos
  // read-only del cliente seleccionado (GET /clientes/{cedula} — mismo patrón
  // de PrestamosPage; réplica del load T000G16/T000G17, factura.cs:2450-2514).
  const [clienteDetalle, setClienteDetalle] = useState<any | null>(null)
  // % IVA de la empresa (GET /cuenta → campo `iva` = EmpresaLibreNumerico1;
  // T3 CuentaController — patrón PrestamosPage para leer la cuenta).
  const [ivaEmpresa, setIvaEmpresa] = useState(0)
  // En EDICIÓN las líneas llegan async (GET /facturas/{nro}): el IVA automático
  // espera a tenerlas para no recalcular con el editor vacío. [RGM] 2026-10-05
  const [detalleCargado, setDetalleCargado] = useState(true)
  useEffect(() => {
    api.get<any[]>(`/clientes?empresa=${empresa}`).then(setClientes).catch(() => undefined)
    api.get<any[]>(`/catalogos/productos?empresa=${empresa}`).then(setProductos).catch(() => undefined)
    api.get<any>('/cuenta?empresa=' + empresa)
      .then((cuenta) => {
        setIvaEmpresa(Number(cuenta?.iva ?? 0))
        setTrabajaFestivo(Number(cuenta?.trabajaFestivo ?? 0) === 1)
      })
      .catch(() => undefined)
  }, [empresa])

  // Refresca el %IVA al abrir el modal: pudo cambiarse en Mi Cuenta sin
  // recargar esta página. [RGM] 2026-10-05 (pedido Rafael: "aplica el IVA
  // sobre el total con descuento"). [RGM] 2026-10-07: también trae el flag
  // trabajaFestivos (regla de Feriados del crédito).
  const refrescarIvaEmpresa = () => {
    api.get<any>('/cuenta?empresa=' + empresa)
      .then((cuenta) => {
        setIvaEmpresa(Number(cuenta?.iva ?? 0))
        setTrabajaFestivo(Number(cuenta?.trabajaFestivo ?? 0) === 1)
      })
      .catch(() => undefined)
  }

  // Datos del cliente al seleccionar: el endpoint (ClientesController.Detalle)
  // expone Nombre, Cédula, Celular, Dirección, Residencia, Zona y Cobrador.
  // No expone CajaNro (en GX sale de la Zona — gxLoad_42, factura.cs:2487-2514;
  // la caja se resuelve por Zona en el backend de facturas).
  const cargarClienteDetalle = (cedula: string) => {
    if (!cedula) { setClienteDetalle(null); return }
    api.get<any>(`/clientes/${cedula}?empresa=${empresa}`)
      .then(setClienteDetalle)
      .catch(() => setClienteDetalle(null))
  }
  const seleccionarCliente = (cedula: string) => {
    setForm((f) => ({ ...f, clienteCedula: cedula }))
    cargarClienteDetalle(cedula)
  }

  const visibleRows = useMemo(() => rows.filter((row) => {
    if (filtroCreador && String(row.facturaCreadaPor ?? '').trim() !== filtroCreador) return false
    return true
  }), [rows, filtroCreador])
  const creadores = useMemo(() => [...new Set(rows.map((r) => String(r.facturaCreadaPor ?? '').trim()).filter(Boolean))], [rows])
  const chips = [filtroCreador ? { clave: 'creador', label: `Creada por: ${filtroCreador}` } : null].filter(Boolean) as { clave: string; label: string }[]
  const limpiarFiltros = () => setFiltroCreador('')
  const quitarFiltro = (clave: string) => { if (clave === 'creador') setFiltroCreador('') }
  const cargar = () => {
    setCargando(true)
    api.get<any[]>(`/facturas?empresa=${empresa}`)
      .then(setRows)
      .catch((e: any) => setError(e.message))
      .finally(() => setCargando(false))
  }
  useEffect(cargar, [empresa])

  const abrirNuevo = () => {
    setEditando(null)
    setErrorModal('')
    // Defaults de alta [RGM] 2026-10-05: Origen=1 (POS), Estado=2 (Activa),
    // FPago=1 (Contado) — pedido Rafael.
    setForm({
      empresaCodigo: empresa, clienteCedula: '', iva: '', porcentajeDescuento: '',
      facturaOrigen: '1', facturaEstado: '2', facturaFPago: '1',
      facturaLibreValor1: '', facturaObservcaiones: '',
    })
    setLineas([{ productoNro: '', cantidad: '' }])
    setClienteDetalle(null)
    setDetalleCargado(true)
    // Crédito inline a sus defaults de alta (feature factura-credito-unificado).
    setCredito(creditoInicial())
    refrescarIvaEmpresa()
    setAbierto(true)
  }

  // Modificar (facturaww.cs:2928-2934): carga el ENCABEZADO. Las líneas no se
  // editan (el BC UPD solo actualiza el encabezado — factura_bc.cs:774-812).
  const abrirEditar = (fila: any) => {
    setErrorModal('')
    setEditando(fila)
    setForm({
      empresaCodigo: fila.empresaCodigo ?? empresa,
      clienteCedula: String(fila.clienteCedula ?? ''),
      iva: String(fila.facturaIVA ?? ''),
      porcentajeDescuento: String(fila.descuento ?? ''),
      facturaOrigen: String(fila.facturaOrigen ?? ''),
      facturaEstado: String(fila.facturaEstado ?? ''),
      facturaFPago: String(fila.facturaFPago ?? ''),
      facturaLibreValor1: String(fila.inicial ?? ''),
      facturaObservcaiones: fila.facturaObservcaiones ?? '',
    })
    setLineas([{ productoNro: '', cantidad: '' }])
    setDetalleCargado(false)
    cargarClienteDetalle(String(fila.clienteCedula ?? ''))
    refrescarIvaEmpresa()
    setAbierto(true)
    // Líneas reales al EDITOR para poder modificarlas/agregar [RGM] 2026-10-05
    // (pedido Rafael): el PUT reemplaza las líneas (patrón FacturaLineaSD) —
    // el UPD del BC GX solo toca el encabezado (factura_bc.cs:774-812).
    api.get<any>(`/facturas/${fila.facturaNro}?empresa=${empresa}`)
      .then((d) => {
        const ls: any[] = d?.lineas ?? []
        setLineas(ls.length > 0
          ? ls.map((l) => ({ productoNro: String(l.productoNro), cantidad: String(l.cantidad) }))
          : [{ productoNro: '', cantidad: '' }])
        setDetalleCargado(true) // con las líneas reales ya corre el IVA automático
      })
      .catch(() => undefined)
  }

  const crear = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorModal('')
    setGuardando(true)
    // [RGM] 2026-10-07 (feature factura-credito-unificado F1): con FPago =
    // Credito (2) y en ALTA, el crédito se crea JUNTO con la factura. Los
    // datos del crédito pendiente se capturan aquí y el POST /prestamos se
    // hace después de guardar la factura (el facturaNro lo liga).
    let creditoPendiente: { facturaNro: number; valor: number } | null = null
    try {
      if (editando) {
        // UPD: la llave va en la URL (factura_bc.cs:756-812, BC000G19)
        await api.put(`/facturas/${editando.facturaNro}?empresa=${empresa}`, {
          clienteCedula: Number(form.clienteCedula),
          facturaIVA: Number(form.iva) || 0,
          facturaOrigen: Number(form.facturaOrigen) || 0,
          facturaEstado: Number(form.facturaEstado) || 0,
          facturaFPago: Number(form.facturaFPago) || 0,
          facturaObservcaiones: form.facturaObservcaiones || undefined,
          facturaLibreValor1: Number(form.facturaLibreValor1) || 0,
          facturaLibreValor3: Number(form.porcentajeDescuento) || 0,
          // Líneas del editor: reemplazo total (ver Modificar del controller —
          // upsert por producto). [RGM] 2026-10-05 (pedido Rafael).
          lineas: lineas.filter((l) => l.productoNro !== '').map((l) => ({
            productoNro: Number(l.productoNro), cantidad: Number(l.cantidad) || 0,
          })),
        })
      } else {
        // [RGM] 2026-10-05 (reporte Rafael: "se deja crear en cero"): la factura
        // exige al menos un producto con cantidad > 0 (mismo texto del backend).
        const lineasValidas = lineas.filter((l) => l.productoNro !== '')
        if (lineasValidas.length === 0 || subTotalCalc <= 0) {
          setErrorModal('La factura debe tener al menos un producto')
          setGuardando(false)
          return
        }
        // [RGM] 2026-10-07 (F1): validaciones DEL CRÉDITO antes de crear la
        // factura — los MISMOS mensajes del modal GX (prestamo.cs; no se
        // deja factura huérfana por datos faltantes del crédito).
        const esCreditoNuevo = Number(form.facturaFPago) === 2
        if (esCreditoNuevo) {
          if (!Number(clienteDetalle?.zonaCodigo ?? 0)) {
            setErrorModal('Seleccione Ruta')
            setGuardando(false)
            return
          }
          if (!credito.fechaProximoCobro) {
            setErrorModal('Asignar Fecha Proximo Cobro')
            setGuardando(false)
            return
          }
          // [RGM] 2026-10-07 (pedido Rafael: "validar en factura valor cuota
          // debe colocarse"): el GX exige el Valor Cuota como atributo
          // requerido en el flujo prestar (prestar.cs:1172) y el backend lo
          // valida igual (ValidacionesNegocio.ValidarValorCuotaAsignado —
          // prestamo_bc.cs:1011-1014, mismo mensaje) → se valida ANTES de
          // crear la factura para no dejar factura sin crédito.
          if (!credito.cuota || Number(credito.cuota) <= 0) {
            setErrorModal('Asignar Valor Cuota')
            setGuardando(false)
            return
          }
        }
        const creada = await api.post<any>('/facturas', {
          empresaCodigo: form.empresaCodigo,
          clienteCedula: Number(form.clienteCedula),
          iva: Number(form.iva) || 0,
          porcentajeDescuento: Number(form.porcentajeDescuento) || 0,
          facturaOrigen: Number(form.facturaOrigen) || 0,
          facturaEstado: Number(form.facturaEstado) || 0,
          facturaFPago: Number(form.facturaFPago) || 0,
          facturaObservcaiones: form.facturaObservcaiones || undefined,
          facturaLibreValor1: Number(form.facturaLibreValor1) || 0,
          lineas: lineas.filter((l) => l.productoNro !== '').map((l) => ({
            productoNro: Number(l.productoNro), cantidad: Number(l.cantidad) || 0,
          })),
        })
        if (esCreditoNuevo) {
          // Valor a financiar = Total − Inicial (misma fórmula GX del BC —
          // factura_bc.cs:1189-1191, la que ya muestra el form).
          creditoPendiente = {
            facturaNro: Number(creada?.facturaNro ?? 0),
            valor: Number(creada?.facturaTotal ?? totalCalc) - (Number(form.facturaLibreValor1) || 0),
          }
        }
      }
      setAbierto(false)
      setEditando(null)
      cargar()
      // Alta del CRÉDITO con el MISMO endpoint/servicio de préstamos del flujo
      // "Configurar créditos" (payload espejo de PrestamosPage.bodyPrestamo;
      // la lógica de préstamos NO se toca — prestamo_bc replica del BC).
      if (creditoPendiente) {
        try {
          const prestamo = await api.post<any>('/prestamos', {
            empresaCodigo: form.empresaCodigo,
            clienteCedula: Number(form.clienteCedula),
            clienteNombre: String(clienteDetalle?.clienteNombre ?? '').trim(),
            metodoPago: Number(credito.metodoPago),
            tipo: 'NUEVO',
            valor: creditoPendiente.valor,
            cuotas: Number(credito.cuotas) || 0,
            cuota: credito.cuota ? Number(credito.cuota) : undefined,
            fechaInicia: credito.fechaProximoCobro || undefined,
            fechaPrestamo: credito.fechaPrestamo || undefined,
            fechaProximoCobro: credito.fechaProximoCobro || undefined,
            // La ruta del préstamo es la del CLIENTE (regla GX prestamo.cs:
            // 5415/6240 — no editable; el modal la precarga igual).
            zonaCodigo: Number(clienteDetalle?.zonaCodigo) || 0,
            prestamoDiaCobro: credito.prestamoDiaCobro || undefined,
            porcentaje: 0,
            aceptaCondenas: credito.aceptaCondenas,
            nroAtrasosPorCondena: Number(credito.nroAtrasosPorCondena) || 0,
            // Flag "diferente a cuota" derivado (misma regla del modal:
            // prestamo_bc.cs:751-754).
            valorCondenaDiferenteA: credito.aceptaCondenas === 'Si'
              && Number(credito.valorCondena) !== (Number(credito.cuota) || 0) ? 1 : 0,
            prestamoValorCondena: Number(credito.valorCondena) || 0,
            feriados: Number(credito.feriados) || 0,
            prestamoLibreNumerico2: Number(credito.prestamoLibreNumerico2) || 0,
            facturaNro: creditoPendiente.facturaNro,
          })
          setAviso(`Factura #${creditoPendiente.facturaNro} creada con crédito (Prestamo #${prestamo?.prestamoNro}).`)
        } catch (e2: any) {
          // La factura YA quedó guardada — el crédito se completa con el botón
          // "Configurar créditos" (flujo legado intacto).
          setError(`Factura #${creditoPendiente.facturaNro} guardada, pero el crédito no se pudo crear: ${e2?.message ?? 'error'}. Usa "Configurar créditos" para completarlo.`)
        }
      }
    } catch (err: any) {
      setErrorModal(err.message)
    } finally {
      setGuardando(false)
    }
  }

  // Eliminar (facturaww.cs:2936-2944 'DO DELETE'): el backend replica el DLT
  // del BC (factura_bc.cs:817-860): borra líneas en cascada y REVIERTE el
  // stock de los productos (factura_bc.cs:1701-1702). Sin 409: el BC no tiene
  // CannotDeleteReferencedRecord (borra en cascada).
  const eliminar = (fila: any) => {
    if (!window.confirm(`¿Eliminar la factura ${fila.facturaNro}?`)) return
    api.delete(`/facturas/${fila.facturaNro}?empresa=${empresa}`)
      .then(() => {
        setAviso('Factura eliminada.')
        cargar()
      })
      .catch((e: any) => setError(e.message))
  }

  const verFactura = (fila: any) => {
    navigate(`/facturas/${fila.facturaNro}`)
  }
  // [RGM] Crédito de la factura: viaja el Nro de la factura para que el alta
  // del préstamo lo guarde oculto en PrestamoFacturaNro (validacreditocreado-
  // defactura.cs:76-88 — una factura solo puede tener un crédito).
  const crearPrestamo = (fila: any) => {
    navigate(`/prestamos?cliente=${fila.clienteCedula}&valor=${fila.aFinanciar ?? ''}&facturaNro=${fila.facturaNro}`)
  }
  // [RGM] 2026-10-07 (diseño Rafael B+C: "¿cómo veo los créditos desde
  // facturación?"): las facturas CON crédito llevan al CRÉDITO del cliente
  // (cuentas por cobrar con plan de cuotas/abonos) — clave en modo Solo
  // Facturación, donde el menú muestra "Créditos" y no "Prestamos".
  const verCredito = (fila: any) => {
    navigate(`/clientes/${fila.clienteCedula}`)
  }
  // [RGM] 2026-10-14 (devoluciones Fase 1): abre el modal compartido de
  // devolución para la fila (misma pieza que usa FacturaViewPage).
  const abrirDevolucion = (fila: any) => {
    setError('')
    setDevolucionDe(fila)
  }

  // [RGM] 2026-10-14 (devoluciones Fase 1): descarga/abre el PDF del documento
  // de devolución (GET /facturas/{nroDoc}/devolucion.pdf) — helper global
  // api.abrirPdf (pestaña nueva, patrón anti-popup).
  const imprimirDevolucion = async (nroDocumento: number) => {
    try {
      await api.abrirPdf(`/facturas/${nroDocumento}/devolucion.pdf?empresa=${empresa}`)
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo abrir el PDF de la devolución')
    }
  }

  const rowActions: WwRowAction[] = [
    // [RGM] 2026-10-01 (pedido Rafael): acciones INLINE con icono (como Cierre)
    // — Ver Factura (lupa) y el crédito con tooltip "Configurar créditos",
    // más transparente: sobre una factura a crédito lo que se hace es
    // configurar el crédito que quedará como préstamo de la ruta.
    { label: 'Ver Factura', onClick: verFactura, placement: 'inline-end', icon: 'cilSearch' },
    // [RGM] 2026-10-07 (B+C): con el crédito configurado, este botón lleva al
    // CRÉDITO del cliente (cuentas por cobrar). [RGM] 2026-10-14 (devoluciones
    // Fase 1): NO aplica a los documentos de devolución (no tienen crédito).
    { label: 'Ver Crédito', onClick: verCredito, placement: 'inline-end', icon: 'cilWallet', mostrar: (fila) => !fila.esDevolucion && Number(fila.facturaFPago) === 2 && !!fila.tieneCredito },
    // Se OCULTA cuando la factura ya tiene su crédito configurado [RGM]
    // 2026-10-05 (pedido Rafael: no crear por error OTRO préstamo a la misma
    // factura — validacreditocreadodefactura.cs:76-88: una factura, un crédito;
    // WwRowAction no tiene estado disabled: se oculta con `mostrar`).
    { label: 'Configurar créditos', onClick: crearPrestamo, placement: 'inline-end', icon: 'cilMoney', mostrar: (fila) => Number(fila.facturaFPago) === 2 && !fila.tieneCredito },
    // [RGM] 2026-10-14 (devoluciones Fase 1 — diseño aprobado): acción
    // "Devolver" (↩) SOLO para admin/superadmin, sobre facturas NO devolución
    // y ni Canceladas (3) ni Devueltas totalmente (4) — AJUSTES punto 4 (el
    // backend valida lo mismo: 403/400 — FacturasController.cs:716-739).
    { label: 'Devolver', onClick: abrirDevolucion, placement: 'inline-end', icon: 'cilActionUndo', mostrar: (fila) => isAdminFactura && !fila.esDevolucion && ![3, 4].includes(Number(fila.facturaEstado)) },
    // [RGM] 2026-10-14 (devoluciones Fase 1): Editar/Eliminar pasan de
    // onEditar/onEliminar a rowActions con `mostrar` para OCULTARLOS en los
    // documentos de devolución (el backend rechaza modificarlos —
    // FacturasController.cs:350-354 — y LibreValor3 es el vínculo, no el %).
    // Mismo par del WwGrid (label/icono/destructive/placement overflow).
    { id: 'editar', label: 'Editar', icon: 'cilPencil', placement: 'overflow', onClick: abrirEditar, mostrar: (fila) => !fila.esDevolucion },
    { id: 'eliminar', label: 'Eliminar', icon: 'cilTrash', destructive: true, placement: 'overflow', onClick: eliminar, mostrar: (fila) => !fila.esDevolucion },
  ]

  // [RGM] 2026-09-29 (pedido Rafael): formulario en TARJETAS + totales en
  // vivo (fiel a GX: Total = IVA + SubTotal − SubTotal × Descuento/100 —
  // factura_bc.cs:924-925).
  const tarjetaFactura = (titulo: string, contenido: React.ReactNode) => (
    <CCard className="mb-3 shadow-sm" style={{ borderRadius: 8 }}>
      <CCardHeader className="fw-semibold py-2">{titulo}</CCardHeader>
      <CCardBody style={{ padding: '14px 16px' }}>
        <CRow className="g-3">{contenido}</CRow>
      </CCardBody>
    </CCard>
  )
  // Montos de la factura con la fuente única utils/moneda.ts (fmtMoneda —
  // símbolo configurado + máscara GX Z,ZZZ,ZZZ,ZZ9.99). [RGM] 2026-10-02 (F)
  const lineasCalc = lineas.map((l) => {
    const pr = productos.find((p) => String(p.productoNro) === String(l.productoNro))
    const valor = Number(pr?.productoValor ?? 0)
    const cant = Number(l.cantidad) || 0
    return { ...l, valor, subtotal: valor * cant }
  })
  const subTotalCalc = lineasCalc.reduce((acc, l) => acc + l.subtotal, 0)
  // Descuento monetario (SubTotal × %Descuento) — fórmula GX (factura_bc.cs:924;
  // factura.cs:2333) junto con Total = IVA + SubTotal − Descuento (:925/:2335).
  const descuentoCalc = subTotalCalc * ((Number(form.porcentajeDescuento) || 0) / 100)
  // Neto = SubTotal − Descuento: base del IVA (pedido Rafael 2026-10-05; el GX
  // lo muestra como campos sueltos: Sub Total :728 · Valor Descuento :742 ·
  // IVA :756 · Total :778 — factura.cs).
  const netoCalc = subTotalCalc - descuentoCalc
  // IVA automático por %IVA de la empresa [RGM] 2026-10-05 (pedido Rafael:
  // "aplica el IVA sobre el total con descuento"): IVA = Neto × %/100,
  // redondeado a 2 decimales — EN ALTA Y EN EDICIÓN. En edición espera a que
  // carguen las líneas reales (detalleCargado) para no recalcular con el
  // editor vacío; con la empresa sin % (0) el IVA queda manual. El monto viaja
  // en el POST/PUT (`iva`) — el contrato no cambia.
  const ivaAutoCalc = Math.round(netoCalc * ivaEmpresa) / 100
  const totalCalc = (Number(form.iva) || 0) + netoCalc
  useEffect(() => {
    if (ivaEmpresa <= 0 || !detalleCargado) return
    setForm((f) => ({ ...f, iva: String(ivaAutoCalc) }))
  }, [ivaEmpresa, detalleCargado, ivaAutoCalc])

  const [totalReg, setTotalReg] = useState(rows.length)
  return (
    <>
      <CRow className="mb-4">
        <CCol>
          <h4>Factura</h4>
          <small className="text-body-secondary">{totalReg} {totalReg === 1 ? 'registro' : 'registros'}</small>
        </CCol>
      </CRow>
      {error && <CAlert color="danger">{error}</CAlert>}
      {aviso && <CAlert color="warning" dismissible onClose={() => setAviso('')}>{aviso}</CAlert>}
      {/* [RGM] 2026-10-14 (devoluciones Fase 1): mensaje de éxito del registro
          con la acción "Imprimir devolución" (PDF del documento devuelto). */}
      {devolucionCreada && (
        <CAlert color="success" className="d-flex flex-wrap align-items-center justify-content-between gap-2"
          dismissible onClose={() => setDevolucionCreada(null)}>
          <span>
            Devolución #{devolucionCreada.devolucionNro} registrada — Total devuelto {fmtMoneda(devolucionCreada.totalDevuelto)}
            {devolucionCreada.devolucionTotal ? ' — factura devuelta totalmente (devolución total)' : ''}.
          </span>
          <CButton size="sm" color="success" variant="outline"
            onClick={() => imprimirDevolucion(devolucionCreada.devolucionNro)}>
            Imprimir devolución
          </CButton>
        </CAlert>
      )}
      <WwGrid
        filters={
          <WwFilterBar
            selects={[
              { key: 'creador', label: 'Todos los Creadores', opciones: creadores, valor: filtroCreador, onChange: setFiltroCreador },
            ]}
            chips={chips}
            onQuitar={quitarFiltro}
            onLimpiar={limpiarFiltros}
          />
        }
        title="Factura"
        columns={columnas}
        rows={visibleRows}
        busquedaPlaceholder="Buscar por factura, cliente o cédula..." 
        onTotalChange={setTotalReg}
        loading={cargando}
        onAgregar={abrirNuevo}
        // Editar/Eliminar viajan en rowActions (gating por fila: se ocultan en
        // los documentos de devolución — [RGM] 2026-10-14).
        rowActions={rowActions}
        // [RGM] 2026-10-05 (pedido Rafael: "tocar sobre la fila factura llevar
        // también al detalle"): clic en la FILA = Ver Factura (misma acción de
        // la lupa; el clic en botones/enlaces no propaga al row — WwGrid.tsx:1283).
        onRowClick={verFactura}
      />

      <CModal visible={abierto} onClose={() => { setAbierto(false); setEditando(null) }} size="lg">
        <CModalHeader>
          <CModalTitle>{editando ? 'Modificar factura' : 'Nueva factura'}</CModalTitle>
        </CModalHeader>
        <CModalBody style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          {errorModal && <CAlert color="danger">{errorModal}</CAlert>}
          <CForm ref={formRef} onSubmit={crear}>
            {tarjetaFactura('Cliente', (
              <>
                {/* [RGM] 2026-09-29 (pedido Rafael): Cliente con NOMBRE (select
                    — facturaww.cs:3755-3802). [RGM] 2026-10-05 (pedido Rafael:
                    "traer datos cliente como GX"): al elegir el cliente se
                    cargan sus datos read-only (load T000G16/T000G17 —
                    factura.cs:2450-2514). */}
                <CCol md={12}>
                  <CFormLabel>Cliente</CFormLabel>
                  <CFormSelect value={form.clienteCedula} onChange={(e) => seleccionarCliente(e.target.value)}>
                    <option value="">Elegir cliente…</option>
                    {clientes.map((c) => (
                      <option key={String(c.clienteCedula)} value={String(c.clienteCedula)}>
                        {`${c.clienteNombre?.trim() || 'Cliente'} — ${c.clienteCedula}`}
                      </option>
                    ))}
                  </CFormSelect>
                </CCol>
                {clienteDetalle && (
                  <>
                    <CCol md={4}><CFormLabel>Nombre</CFormLabel><CFormInput readOnly disabled value={String(clienteDetalle.clienteNombre ?? '').trim()} /></CCol>
                    <CCol md={4}><CFormLabel>Cédula</CFormLabel><CFormInput readOnly disabled value={String(clienteDetalle.clienteCedula ?? '')} /></CCol>
                    <CCol md={4}><CFormLabel>Celular</CFormLabel><CFormInput readOnly disabled value={String(clienteDetalle.clienteCelular ?? '').trim()} /></CCol>
                    <CCol md={6}><CFormLabel>Dirección</CFormLabel><CFormInput readOnly disabled value={String(clienteDetalle.clienteDireccion ?? '').trim()} /></CCol>
                    <CCol md={6}><CFormLabel>Residencia</CFormLabel><CFormInput readOnly disabled value={String(clienteDetalle.clienteReisdencia ?? '').trim()} /></CCol>
                    <CCol md={12}><CFormLabel>Zona</CFormLabel><CFormInput readOnly disabled value={String(clienteDetalle.zonaNombre ?? '').trim()} /></CCol>
                  </>
                )}
              </>
            ))}
            {tarjetaFactura('Factura', (
              <>
                {/* [RGM] 2026-10-05 (pedido Rafael): defaults de ALTA
                    Origen=1 (POS) y Estado=2 (Activa), editables
                    (facturaencabezadosd.cs:2892-2893; factura.cs:5561-5563).
                    En edición se conservan los de la factura. */}
                <CCol md={4}>
                  <CFormLabel>Origen</CFormLabel>
                  <CFormSelect value={form.facturaOrigen} onChange={(e) => setForm({ ...form, facturaOrigen: e.target.value })}>
                    <option value="">Elegir…</option>
                    {Object.entries(ORIGEN_FACTURA).map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                  </CFormSelect>
                </CCol>
                <CCol md={4}>
                  <CFormLabel>Estado</CFormLabel>
                  <CFormSelect value={form.facturaEstado} onChange={(e) => setForm({ ...form, facturaEstado: e.target.value })}>
                    <option value="">Elegir…</option>
                    {Object.entries(ESTADO_FACTURA).map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                  </CFormSelect>
                </CCol>
                <CCol md={4}>
                  <CFormLabel>FPago</CFormLabel>
                  <CFormSelect value={form.facturaFPago} onChange={(e) => setForm({ ...form, facturaFPago: e.target.value })}>
                    <option value="">Elegir…</option>
                    {Object.entries(FPAGO_FACTURA).map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                  </CFormSelect>
                </CCol>
                {/* [RGM] 2026-10-05 (pedido Rafael): Inicial y % Descuento ARRIBA
                    con los demás inputs; los totales quedan en fila al pie
                    (tarjeta "Totales"). Descuento = SubTotal × %/100
                    (factura_bc.cs:924). El % Descuento es un entero (control
                    GX ZZZ9 — factura.cs:880). */}
                <CCol md={4}>
                  <CFormLabel>Inicial</CFormLabel>
                  <CInputGroup>
                    <CInputGroupText>{simboloMoneda()}</CInputGroupText>
                    <NumericFormat
                      className="form-control"
                      value={form.facturaLibreValor1}
                      onValueChange={(v) => setForm({ ...form, facturaLibreValor1: v.value })}
                      thousandSeparator="," decimalSeparator="." decimalScale={2} fixedDecimalScale
                      allowNegative={false} inputMode="decimal"
                    />
                  </CInputGroup>
                </CCol>
                {Number(form.facturaFPago) === 2 && (
                  <CCol md={4}>
                    <CFormLabel>a financiar</CFormLabel>
                    <CFormInput value={fmtMoneda(totalCalc - (Number(form.facturaLibreValor1) || 0))} readOnly disabled />
                  </CCol>
                )}
                <CCol md={4}><CFormLabel>% Descuento</CFormLabel><CFormInput type="number" min="0" step="1" value={form.porcentajeDescuento} onChange={(e) => setForm({ ...form, porcentajeDescuento: e.target.value })} /></CCol>
                {/* [RGM] 2026-10-07 (pedido Rafael: "en factura observaciones
                    van al final"): Observaciones se movió al FINAL del form. */}
              </>
            ))}
            {/* Líneas editables también al MODIFICAR [RGM] 2026-10-05 (pedido
                Rafael: "no me deja editar productos ni agregar línea"): el UPD
                del BC GX solo toca el encabezado (factura_bc.cs:774-812,
                BC000G19); el PUT de la réplica reemplaza las líneas
                (upsert por producto — patrón FacturaLineaSD). */}
            {tarjetaFactura('Productos', (
              <>
                {lineasCalc.map((l, i) => (
                  <CRow key={i} className="g-2 mb-2 align-items-end">
                    <CCol md={4}>
                      <CFormLabel>Producto</CFormLabel>
                      {/* Select con búsqueda [RGM] 2026-10-05 (pedido Rafael:
                          "buscar producto en el mismo select"). */}
                      <SelectorProducto
                        productos={productos}
                        value={l.productoNro}
                        disabled={guardando}
                        onChange={(v) => setLineas(lineas.map((x, j) => j === i ? { ...x, productoNro: v } : x))}
                      />
                    </CCol>
                    <CCol md={1}><CFormLabel>Cantidad</CFormLabel><CFormInput type="number" value={l.cantidad} onChange={(e) => setLineas(lineas.map((x, j) => j === i ? { ...x, cantidad: e.target.value } : x))} /></CCol>
                    <CCol md={2}><CFormLabel>Valor</CFormLabel><CFormInput value={fmtMoneda(l.valor)} readOnly disabled /></CCol>
                    {/* [RGM] 2026-10-07 (pedido Rafael: "grilla item productos
                        mostrar stock"): columna Stock del producto elegido
                        (productoStock del catálogo — CatalogosController). */}
                    <CCol md={2}>
                      <CFormLabel>Stock</CFormLabel>
                      <CFormInput
                        value={l.productoNro
                          ? String(productos.find((p) => String(p.productoNro) === String(l.productoNro))?.productoStock ?? 0)
                          : ''}
                        readOnly disabled
                      />
                    </CCol>
                    <CCol md={2}><CFormLabel>Subtotal</CFormLabel><CFormInput value={fmtMoneda(l.subtotal)} readOnly disabled /></CCol>
                    <CCol md={1}>
                      {lineas.length > 1 && (
                        <CButton color="danger" size="sm" type="button" disabled={guardando} onClick={() => setLineas(lineas.filter((_, j) => j !== i))}>Quitar</CButton>
                      )}
                    </CCol>
                  </CRow>
                ))}
                <div className="mt-2">
                  <CButton color="secondary" size="sm" type="button" disabled={guardando} onClick={() => setLineas([...lineas, { productoNro: '', cantidad: '' }])}>Agregar línea</CButton>
                </div>
              </>
            ))}
            {/* [RGM] 2026-10-05 (pedido Rafael): totales al PIE de la factura,
                en COLUMNA alineada a la derecha: SubTotal · Descuento · SubTotal
                con Descuento (neto, base del IVA) · IVA · Total. Réplica del
                formulario GX que muestra Sub Total (:728), Valor Descuento
                (:742, read-only), IVA (:756) y Total (:778) — factura.cs. El
                neto es agregado de Rafael para visualizar la base del IVA.
                Descuento = SubTotal × %/100 (factura_bc.cs:924); Total = IVA +
                neto = IVA + SubTotal − Descuento (factura_bc.cs:925). Los
                montos salen de las líneas del editor (también al modificar,
                que ahora carga las líneas reales — el UPD del BC GX es de
                encabezado, factura_bc.cs:774-812). */}
            {tarjetaFactura('Totales', (
              <div className="ms-auto d-flex flex-column gap-1" style={{ minWidth: 320 }}>
                <div className="d-flex justify-content-between gap-4">
                  <span className="text-body-secondary">SubTotal</span>
                  <span className="fw-semibold">{fmtMoneda(subTotalCalc)}</span>
                </div>
                <div className="d-flex justify-content-between gap-4">
                  <span className="text-body-secondary">Descuento</span>
                  <span className="fw-semibold">{descuentoCalc > 0 ? `− ${fmtMoneda(descuentoCalc)}` : fmtMoneda(0)}</span>
                </div>
                <div className="d-flex justify-content-between gap-4">
                  <span className="text-body-secondary">SubTotal con Descuento</span>
                  <span className="fw-semibold">{fmtMoneda(netoCalc)}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center gap-4">
                  <span className="text-body-secondary">IVA{ivaEmpresa > 0 ? ` (${ivaEmpresa}%)` : ''}</span>
                  {ivaEmpresa > 0 ? (
                    <span className="fw-semibold">{fmtMoneda(Number(form.iva) || 0)}</span>
                  ) : (
                    <CInputGroup size="sm" style={{ width: 150 }}>
                      <CInputGroupText>{simboloMoneda()}</CInputGroupText>
                      <NumericFormat
                        className="form-control"
                        value={form.iva}
                        onValueChange={(v) => setForm({ ...form, iva: v.value })}
                        thousandSeparator="," decimalSeparator="." decimalScale={2} fixedDecimalScale
                        allowNegative={false} inputMode="decimal"
                      />
                    </CInputGroup>
                  )}
                </div>
                <div className="d-flex justify-content-between align-items-center gap-4 border-top pt-2 mt-1">
                  <span className="fw-bold">TOTAL</span>
                  <span className="fs-5 fw-bold">{fmtMoneda(totalCalc)}</span>
                </div>
              </div>
            ))}
            {/* [RGM] 2026-10-07 (feature factura-credito-unificado F1 — pedido
                Rafael): con FPago = Credito el crédito se configura EN ESTA
                MISMA pantalla — configurables del modal "Configurar créditos"
                (PrestamosPage) MENOS los que ya viajan de la factura (cliente
                y su ruta, valor a financiar, factura Nro, empresa). Al Guardar
                se crea la factura y el crédito juntos con el MISMO endpoint de
                préstamos (lógica intacta). Solo en ALTA: en edición el crédito
                se maneja como siempre (botón "Configurar créditos"). */}
            {!editando && Number(form.facturaFPago) === 2 && tarjetaFactura('Crédito', (
              <>
                <CCol md={4}>
                  <CFormLabel>Modalidad de pago</CFormLabel>
                  <CFormSelect value={credito.metodoPago} onChange={(e) => {
                    const mp = e.target.value
                    // Pago Libre (60): el campo Cuotas se OCULTA (regla actual
                    // del modal — el BC exige Cuotas=0, prestamo_bc.cs:987-989).
                    setCredito({ ...credito, metodoPago: mp, ...(mp === '60' ? { cuotas: '' } : {}) })
                  }}>
                    {Object.entries(METODO_PAGO_CREDITO).map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                  </CFormSelect>
                </CCol>
                <CCol md={4}>
                  <CFormLabel>Dia Cobro</CFormLabel>
                  <CFormSelect value={credito.prestamoDiaCobro} onChange={(e) => setCredito({ ...credito, prestamoDiaCobro: e.target.value })}>
                    <option value="">(Ninguno)</option>
                    {DIA_COBRO_CREDITO.map((v) => (
                      <option key={v} value={v}>{DIA_COBRO_CREDITO_LABEL[v] ?? v}</option>
                    ))}
                  </CFormSelect>
                </CCol>
                <CCol md={4}>
                  <CFormLabel>Fecha Prestamo</CFormLabel>
                  {/* GX: "Fecha Prestamo" editable para el administrador
                      (prestamo.cs:6359 edtPrestamoFecha_Enabled = 1); los demás
                      roles la ven automática (hoy) — mismo criterio del modal. */}
                  {isAdminFactura
                    ? <CFormInput type="date" value={credito.fechaPrestamo} onChange={(e) => setCredito({ ...credito, fechaPrestamo: e.target.value })} />
                    : <CFormInput readOnly disabled value="Automatica (hoy)" />}
                </CCol>
                {credito.metodoPago !== '60' && (
                  <CCol md={4}>
                    <CFormLabel>Cuotas</CFormLabel>
                    <CFormInput type="number" value={credito.cuotas} onChange={(e) => setCredito({ ...credito, cuotas: e.target.value })} />
                  </CCol>
                )}
                <CCol md={4}>
                  <CFormLabel>Cuota</CFormLabel>
                  <CInputGroup>
                    <CInputGroupText>{simboloMoneda()}</CInputGroupText>
                    <NumericFormat
                      className="form-control"
                      value={credito.cuota}
                      onValueChange={(v) => setCredito({ ...credito, cuota: v.value })}
                      thousandSeparator="," decimalSeparator="." decimalScale={2} fixedDecimalScale
                      allowNegative={false} inputMode="decimal"
                    />
                  </CInputGroup>
                </CCol>
                <CCol md={4}>
                  <CFormLabel>Proximo Cobro</CFormLabel>
                  <CFormInput type="date" value={credito.fechaProximoCobro} onChange={(e) => setCredito({ ...credito, fechaProximoCobro: e.target.value })} />
                </CCol>
                <CCol md={4}>
                  <CFormLabel>Inicia Pagos</CFormLabel>
                  {/* GX: en el ALTA el BC deriva FechaInicia = Proximo Cobro
                      (prestamo_bc.cs:759/1036) — se muestra el mismo valor. */}
                  <CFormInput readOnly disabled value={credito.fechaProximoCobro || '—'} />
                </CCol>
                <CCol md={4}>
                  <CFormLabel>Acepta Condenas? (Intereses x mora)</CFormLabel>
                  <CFormSelect value={credito.aceptaCondenas} onChange={(e) => setCredito({ ...credito, aceptaCondenas: e.target.value })}>
                    <option value="No">No</option>
                    <option value="Si">Si</option>
                  </CFormSelect>
                </CCol>
                {credito.aceptaCondenas === 'Si' && (
                  <>
                    <CCol md={4}>
                      <CFormLabel>Valor Condena</CFormLabel>
                      <CInputGroup>
                        <CInputGroupText>{simboloMoneda()}</CInputGroupText>
                        <NumericFormat
                          className="form-control"
                          value={credito.valorCondena}
                          onValueChange={(v) => setCredito({ ...credito, valorCondena: v.value })}
                          thousandSeparator="," decimalSeparator="." decimalScale={2} fixedDecimalScale
                          allowNegative={false} inputMode="decimal"
                        />
                      </CInputGroup>
                    </CCol>
                    <CCol md={4}>
                      <CFormLabel>Nro Atrasos por Condena</CFormLabel>
                      <CFormInput type="number" value={credito.nroAtrasosPorCondena} onChange={(e) => setCredito({ ...credito, nroAtrasosPorCondena: e.target.value })} />
                    </CCol>
                  </>
                )}
                {!trabajaFestivo && (
                  <CCol md={4}>
                    <CFormLabel>Feriados</CFormLabel>
                    <CFormInput type="number" value={credito.feriados} onChange={(e) => setCredito({ ...credito, feriados: e.target.value })} />
                  </CCol>
                )}
                {/* [RGM] 2026-10-07 (pedido Rafael: "cliente solicita renovar
                    quitar, pero si creo préstamo"): el combo Cliente Solicita
                    Renovar? NO va en el crédito de factura (solo existe en el
                    alta normal de préstamos). */}
              </>
            ))}
            {/* [RGM] 2026-10-07 (pedido Rafael: "en factura observaciones van
                al final"): Observaciones al FINAL del formulario. */}
            {tarjetaFactura('Observaciones', (
              <CCol md={12}>
                <CFormInput value={form.facturaObservcaiones} onChange={(e) => setForm({ ...form, facturaObservcaiones: e.target.value })} onBlur={(e) => setForm({ ...form, facturaObservcaiones: capitalizarPalabras(e.target.value) })} />
              </CCol>
            ))}
          </CForm>
        </CModalBody>
        <CModalFooter className="border-top">
          <CButton color="secondary" type="button" disabled={guardando} onClick={() => setAbierto(false)}>Cancelar</CButton>
          <CButton color="primary" type="button" disabled={guardando} onClick={() => formRef.current?.requestSubmit()}>{guardando ? <CSpinner size="sm" /> : 'Guardar'}</CButton>
        </CModalFooter>
      </CModal>

      {/* [RGM] 2026-10-14 (devoluciones Fase 1): modal compartido (misma pieza
          de FacturaViewPage). Al crear: cierra, refresca la grilla y deja el
          mensaje de éxito con "Imprimir devolución" (arriba). */}
      <DevolucionFacturaModal
        facturaNro={devolucionDe?.facturaNro ?? null}
        empresa={empresa}
        onClose={() => setDevolucionDe(null)}
        onCreada={(res) => {
          setDevolucionDe(null)
          setDevolucionCreada(res)
          cargar()
        }}
      />
    </>
  )
}

export default FacturasPage
